import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";

import { baseUrl } from "../../constant/constant";
import { showError, showSuccess } from "../../stores/slices/commonSlice"; // Update path if needed

/* =========================================================================
 *  Ticket Lookup landing page.
 *
 *  Two modes:
 *    'id'    — enter confirmation_id, navigate to /lookup/{id} on submit
 *    'email' — paste the email used at checkout, server queues a resend
 *
 *  Right rail is the yellow promo panel (hidden on mobile). The whole
 *  page is unauthenticated; the actual lookup + resend endpoints
 *  (TicketController::publicLookup / resendTicketEmail) live under
 *  the 'ticket-lookup' rate limiter so an attacker can't enumerate
 *  confirmation ids or use the resend endpoint as an email blaster.
 * ========================================================================= */
export default function Lookup() {
    const [mode, setMode] = useState("id"); // 'id' | 'email'
    const [confirmationId, setConfirmationId] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [finding, setFinding] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Fetch the order here (rather than navigating blindly) so the button can
    // show a loading state until the result is available. On success we hand
    // the order to the result page via router state so it doesn't refetch; on
    // failure we surface the error and stay on the form.
    const handleIdSubmit = async (e) => {
        e.preventDefault();
        const id = confirmationId.trim().toUpperCase();
        if (!id || finding) return;
        setFinding(true);
        try {
            const res = await fetch(
                `${baseUrl}/ticket-lookup/${encodeURIComponent(id)}`
            );
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json?.message || "Ticket not found.");
            }
            navigate(`/lookup/${id}`, { state: { order: json.data || null } });
        } catch (err) {
            dispatch(showError(err.message || "Ticket not found."));
        } finally {
            setFinding(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${baseUrl}/resend-ticket`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim() }),
            });
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json?.message || "Could not send the email.");
            }
            // Endpoint intentionally returns the same generic success message
            // whether or not it found a ticket — that's the anti-enumeration
            // contract on the server side. Surface it verbatim.
            dispatch(showSuccess(json.message));
            setEmail("");
            setMode("id");
        } catch (err) {
            dispatch(showError(err.message || "Could not send the email."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row min-h-[calc(100vh-80px)] bg-white">

            {/* ============== LEFT: form ============== */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16 lg:p-24 relative">
                {mode === "id" ? (
                    <div className="w-full max-w-lg text-center">
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
                            Ticket Lookup
                        </h1>
                        <p className="text-gray-600 font-medium mb-10 text-lg">
                            To check your ticket, please enter your ticket's
                            confirmation ID.
                        </p>

                        <form onSubmit={handleIdSubmit} className="space-y-6">
                            <input
                                type="text"
                                value={confirmationId}
                                onChange={(e) => setConfirmationId(e.target.value.toUpperCase())}
                                placeholder="Enter confirmation id ..."
                                className="w-full bg-white border border-gray-300 rounded-xl px-6 py-4 focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-gray-900 font-medium placeholder-gray-400 shadow-sm text-lg uppercase"
                            />
                            <button
                                type="submit"
                                disabled={!confirmationId.trim() || finding}
                                className="w-full bg-[#ffea00] hover:bg-[#e6d300] border-none disabled:opacity-50 disabled:cursor-not-allowed text-black font-black py-3 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-1 text-lg flex items-center justify-center relative z-20"
                            >
                                {finding ? (
                                    <>
                                        <Loader2 size={20} className="mr-2 animate-spin" /> Finding…
                                    </>
                                ) : (
                                    "Find Ticket"
                                )}
                            </button>
                        </form>
         
                        <button
                            type="button"
                            onClick={() => setMode("email")}
                            className="mt-8 text-gray-500 hover:text-black bg-transparent border-none font-bold transition-colors "
                            style={{borderBottom:"3px solid #6B7280"}}
                        >
                            Lost your Confirmation ID? Resend to Email
                        </button>
                    </div>
                ) : (
                    <div className="w-full max-w-lg text-center">
                        <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Mail size={40} className="text-blue-500" />
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">
                            Resend Ticket
                        </h1>
                        <p className="text-gray-600 font-medium mb-10 text-lg">
                            Enter the email address you used at checkout, and we'll
                            resend your secure ticket link.
                        </p>

                        <form onSubmit={handleEmailSubmit} className="space-y-6">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                className="w-full bg-white border border-gray-300 rounded-xl px-6 py-5 focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-gray-900 font-medium placeholder-gray-400 shadow-sm text-lg"
                            />
                            <button
                                type="submit"
                                disabled={submitting || !email}
                                className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-300 text-white font-black py-4 rounded-xl transition-all shadow-md hover:-translate-y-1 disabled:hover:translate-y-0 disabled:cursor-not-allowed text-lg"
                            >
                                {submitting ? "Sending..." : "Send Recovery Link"}
                            </button>
                        </form>

                        <button
                            type="button"
                            onClick={() => setMode("id")}
                            className="mt-8 flex items-center justify-center mx-auto text-gray-500 hover:text-black font-bold transition-colors group"
                        >
                            <ArrowLeft
                                size={16}
                                className="mr-2 group-hover:-translate-x-1 transition-transform"
                            />{" "}
                            Back to Confirmation ID Lookup
                        </button>
                    </div>
                )}
            </div>

            {/* ============== RIGHT: yellow promo ============== */}
            <div className="lg:flex w-full lg:w-1/2 bg-[#ffea00] p-8 md:p-16 flex-col items-center justify-center relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center text-center w-full max-w-lg mx-auto">
                    <img
                        src="/assets/images/logo_new_color_rec.png"
                        alt="Apsession"
                        className="h-16 md:h-20 w-auto object-contain mb-6 drop-shadow-md"
                    />
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-black tracking-tight mb-12 leading-tight">
                        Your Ticket to Entertainment
                    </h2>
                    {/* //////////////////////////// */}
                            <div className="relative flex justify-center">
                              <div className="absolute inset-0 bg-[#ffea00] rounded-full blur-[100px] opacity-40 w-3/4 h-3/4 m-auto"></div>
                              <img
                                // src={Traingirl}
                                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                alt="Happy user with app"
                                className="relative z-10 w-full max-w-md rounded-[3rem] object-cover h-[450px] sm:h-[550px]"
                                style={{
                                  boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                                  border: '10px solid white',
                                }}
                              />
                              <div className="absolute bottom-10 sm:bottom-16 left-0 md:-left-12 bg-white p-3 rounded-3xl shadow-2xl z-20 w-48 sm:w-56 rotate-[-6deg] border border-gray-100">
                                <div className="bg-gray-50 h-72 sm:h-80 rounded-2xl overflow-hidden relative border border-gray-100">
                                  <img src="/assets/images/ticktcard.png" className="w-full h-24 sm:h-32 object-cover opacity-90" alt="app event header" />
                                  <div className="px-4 py-2 bg-white text-center rounded-t-2xl absolute bottom-0 w-full shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
                                    <p className="font-black text-sm mb-2 text-gray-900">Admit One</p>
                                    <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto bg-[url('https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg')] bg-cover rounded-lg mb-2 opacity-80"></div>
                                    <p className="text-[11px] font-bold text-gray-400">#APS-123456</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                        {/* //////////////////////////// */}
                    {/* <img
                        src="/assets/images/girl-phone.png"
                        alt="Girl with Phone"
                        className="w-full max-w-sm object-contain drop-shadow-xl"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    /> */}
                </div>
            </div>
        </div>
    );
}
